import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { Spinner } from '@/components/ui';
import { useSession } from '@/features/auth/api/auth.api';
import { can } from '@/features/auth/permissions';
import { AccessLevel, UserRole, type PermissionSubject } from '@/types/domain';

/** The one page a customer following a job may see. */
export const PORTAL_PATH = '/portal';

function FullPageSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-canvas">
      <Spinner size={22} label="Loading" className="text-content-muted" />
    </div>
  );
}

/**
 * Gate for everything behind sign-in.
 *
 * The session is resolved before any child renders, so a protected page never
 * flashes on screen for a signed-out visitor. The attempted path is carried in
 * router state, so sign-in can return them where they were headed.
 */
export function RequireAuth() {
  const { data: user, isPending } = useSession();
  const location = useLocation();

  if (isPending) {
    return <FullPageSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  /**
   * A customer following a job has exactly one page, and this is what puts
   * them on it from wherever they arrived - a bookmarked `/jobs`, the `/`
   * they get after signing in, a stale tab.
   *
   * First, before the change-password rule below, and that order is load
   * bearing. A guest is never asked to change their password - the credential
   * is read to them over the phone, so a forced change is a dead end - but if
   * the two rules were the other way round and one ever were, each would
   * bounce the visitor straight back to the other.
   */
  if (user.role === UserRole.JOB_GUEST) {
    return location.pathname.startsWith(PORTAL_PATH) ? (
      <Outlet />
    ) : (
      <Navigate to={PORTAL_PATH} replace />
    );
  }

  /**
   * A generated or reset password is a first-login credential. Until it is
   * changed the only reachable page is the change-password screen - otherwise
   * accounts would keep running on a password their administrator knows.
   */
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
}

/** Restricts a branch of the route tree to specific roles. */
export function RequireRole({ roles, children }: { roles: UserRole[]; children?: ReactNode }) {
  const { data: user, isPending } = useSession();

  if (isPending) {
    return <FullPageSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!roles.includes(user.role)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/**
 * Restricts a branch of the route tree to people holding a permission.
 *
 * The navigation already hides what somebody may not reach, but a typed URL
 * and a stale bookmark do not consult the navigation - and landing on a page
 * that renders and then answers 403 to every request is worse than being told
 * plainly. The API refuses either way; this is what makes the refusal legible.
 */
export function RequirePermission({
  subject,
  level = AccessLevel.READ,
  children,
}: {
  subject: PermissionSubject;
  level?: AccessLevel;
  children?: ReactNode;
}) {
  const { data: user, isPending } = useSession();

  if (isPending) {
    return <FullPageSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!can(user.permissions, subject, level)) {
    return <Navigate to="/forbidden" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/** Keeps an already-signed-in user off the sign-in page. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { data: user, isPending } = useSession();

  if (isPending) {
    return <FullPageSpinner />;
  }

  if (user) {
    /* `/` is the firm's overview, which a guest may not see - and sending
       them there only to be bounced would flash a page they have no business
       loading at all. */
    const home =
      user.role === UserRole.JOB_GUEST
        ? PORTAL_PATH
        : user.mustChangePassword
          ? '/change-password'
          : '/';

    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
